import { IAssertPayload } from '@shared/asserts/i_assert_payload';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { DTO__number_range } from '@shared/dto/generation/settings/DTO__number_range';

export type CallbackFindValue<T> = (value: T, index?: number, obj?: T[]) => boolean;

declare global {
  interface Array<T> {
    are_first_two_values_equal(): boolean;

    find_or_default(predicate: CallbackFindValue<T>, default_value: T): T;

    find_or_assert(predicate: CallbackFindValue<T>, payload?: IAssertPayload): T;

    is_empty(): boolean;

    last(): T | undefined;

    to_set(): Set<T>;

    unique_values(): T[];

    move_item(index_from: number, index_to: number): void;

    insert_at(index: number, item: T): void;
  }
}

Array.prototype.are_first_two_values_equal = function (): boolean {
  return this.length > 1 && this[0] === this[1];
};

Array.prototype.is_empty = function (): boolean {
  return this.length == 0;
};

Array.prototype.unique_values = function <T>(): T[] {
  return Array.from(new Set(this));
};

Array.prototype.last = function <T>(): T | undefined {
  return this.length > 0 ? this[this.length - 1] : undefined;
};

Array.prototype.find_or_default = function <T>(predicate: CallbackFindValue<T>, default_value: T): T {
  const result = this.find(predicate);

  return result ?? default_value;
};

Array.prototype.find_or_assert = function <T>(predicate: CallbackFindValue<T>, payload: IAssertPayload = {}): T {
  const result = this.find(predicate);

  assert_not_null(result, payload);

  return result as T;
};

Array.prototype.is_empty = function (): boolean {
  return this.length == 0;
};

Array.prototype.last = function <T>(): T | undefined {
  return this.length > 0 ? this[this.length - 1] : undefined;
};

Array.prototype.to_set = function <T>(): Set<T> {
  return new Set(this);
};

Array.prototype.unique_values = function <T>(): T[] {
  return Array.from(new Set(this));
};

/**
 * Moves an item from one index to another within the same array (in-place).
 * * @param this - The array to modify
 * @param index_from - The current index of the item
 * @param index_to - The target index where the item should move
 * @returns The modified array
 */
Array.prototype.move_item = function <T>(index_from: number, index_to: number): void {
  // Guard clause: Validate indices are within bounds
  let index_range = new DTO__number_range(0, this.length - 1);
  index_range.assert_number_in_range(index_from);
  index_range.assert_number_in_range(index_to);

  // If the indices are the same, no movement is needed
  if (index_from === index_to) return;

  // 1. Remove the item from its original position
  const [item] = this.splice(index_from, 1);

  // 2. Insert the item into its new position
  this.splice(index_to, 0, item);

  return;
};

Array.prototype.insert_at = function <T>(index: number, item: T): void {
  this.splice(index, 0, item);
};

export {}; // This ensures TypeScript treats it as a module
